/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, useState } from "react";

import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import { bindActionCreators, Dispatch } from "redux";

import invert from "lodash/invert";
import moment from "moment";
import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IconButton,
  IColumn,
  IDetailsRowProps,
  Modal,
  SelectionMode,
  Text,
} from "@fluentui/react";

import * as AccessControlActions from "Dataflows/AccessControl/AccessControlAction";
import * as ClRegionActions from "Dataflows/ClRegion/ClRegionAction";

import AccessControlDetails from "Components/AccessControlDetails/AccessControlDetails";
import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import {
  detailListStyles,
  iconButtonStyles,
  modalHeaderStyles,
  modalStyles,
  ModalContent,
  ModalHeader,
  StyledSpan,
} from "Components/Common/SharedListStyles.styles";
import StatusElement from "Components/Common/Status/StatusElement";
import {
  accessControlMultiple,
  accessControlUserRole,
  accessControlUserStatusTypes,
} from "Constants/AccessControlConstants";
import { SubSiteUrlRootPath, TenantTypesEnum } from "Constants/TenantTypes";
import { AppState } from "Dataflows/Reducers";
import { IAccessControlUser } from "Interfaces/AccessControl/IAccessControlUser";

import { PaginationRow } from "../Common/Pagination/Pagination.styles";
import "../Common/Pagination/Pagination.styles.css";
import { IAccessControlListProps } from "./IAccessControlListProps";

/**
 * List of admin user profiles
 * @Component
 * @returns {React.FunctionComponent<IAccessControlListProps>}
 */
const AccessControlList: React.FunctionComponent<IAccessControlListProps> = (
  props: IAccessControlListProps
) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { actions, data, pageSize, tenant, totalCount, currentPage } = props;

  const history = useHistory();

  useEffect(() => {
    actions.loadCLRegions();
  }, []);

  const formatModifiedByDate = (incomingDate: Date): React.ReactElement => {
    if (incomingDate === null) return <span>{}</span>;
    const formatedDate = moment(incomingDate).format("MM/DD/YYYY");
    return <span>{formatedDate}</span>;
  };

  const formatStatusColumn = (statusKey: string): React.ReactElement => {
    return <StatusElement statusValue={statusKey} />;
  };

  const formatRolesColumn = (roles: string[]): React.ReactElement => {
    const leng = roles.length;
    if (leng > 1) {
      const roleNames = roles.join(", ");
      return <span title={roleNames}>{accessControlMultiple}</span>;
    } else if (leng == 1) {
      const role = accessControlUserRole[tenant].find((x) => x.key === roles[0]);
      return <span>{role.text}</span>;
    }
    return <span>{}</span>;
  };

  const formatTechAreaColumn = (techAreas: string[]): React.ReactElement => {
    const leng = techAreas.length;
    if (leng > 1) {
      const techAreaNames = techAreas.join(", ");
      return <span title={techAreaNames}>{accessControlMultiple}</span>;
    } else if (leng == 1) {
      return <span>{techAreas[0]}</span>;
    }
    return <span>{}</span>;
  };

  const columns: IColumn[] = [
    {
      key: "id",
      name: "ID",
      ariaLabel: "Access Control ID",
      fieldName: "id",
      minWidth: 10,
      maxWidth: 20,
    },
    {
      key: "firstName",
      name: "First Name",
      ariaLabel: "First Name",
      fieldName: "firstName",
      minWidth: 80,
      maxWidth: 100,
      isResizable: true,
      isSorted: false,
    },
    {
      key: "lastName",
      name: "Last Name",
      ariaLabel: "Last Name",
      fieldName: "lastName",
      minWidth: 80,
      maxWidth: 100,
    },
    {
      key: "email",
      name: "Email",
      ariaLabel: "Email",
      fieldName: "emailAddress",
      minWidth: 100,
      maxWidth: 150,
      isResizable: true,
    },
    {
      key: "role",
      name: "Role",
      ariaLabel: "Role",
      fieldName: "roleNames",
      minWidth: 100,
      maxWidth: 110,
      onRender: (item: IAccessControlUser): React.ReactElement => {
        const selectedTenant = tenant === TenantTypesEnum.MLSA ? "msa" : tenant.toLowerCase();
        return formatRolesColumn(item.roleNames.filter((r) => r.includes(selectedTenant)));
      },
    },
    {
      key: "region",
      name: "Region",
      ariaLabel: "Region",
      fieldName: "countryString",
      minWidth: 80,
      maxWidth: 90,
    },
    {
      key: "status",
      name: "Status",
      ariaLabel: "Status",
      fieldName: "isActive",
      minWidth: 80,
      maxWidth: 90,
      onRender: (item: IAccessControlUser): React.ReactElement => {
        return formatStatusColumn(
          item.isActive
            ? accessControlUserStatusTypes.ACTIVE
            : accessControlUserStatusTypes.INACTIVE
        );
      },
    },
    {
      key: "dateModified",
      name: "Last Modified",
      ariaLabel: "Last Modified",
      fieldName: "dateModified",
      minWidth: 100,
      maxWidth: 150,
      onRender: (item: IAccessControlUser): React.ReactElement => {
        return formatModifiedByDate(item.dateModified);
      },
    },
    {
      key: "modifiedBy",
      name: "Last Modified By",
      ariaLabel: "Last Modified By",
      fieldName: "modifiedBy",
      minWidth: 100,
      maxWidth: 150,
    },
  ];

  const mvpColumns: IColumn[] = [
    ...columns,
    {
      key: "technologyFocusArea",
      name: "Tech Area",
      ariaLabel: "Technology Focus Area",
      fieldName: "technologyFocusAreas",
      minWidth: 100,
      maxWidth: 150,
      onRender: (item: IAccessControlUser): React.ReactElement => {
        return formatTechAreaColumn(item.technologyFocusAreas);
      },
    },
  ];

  const onClickEvent = (userId: number, selectedUser: IAccessControlUser): void => {
    actions.updateSelectedUserId(userId);
    actions.updateSelectedUser(selectedUser);
    history.push(`/${invert(SubSiteUrlRootPath)[tenant]}/access/${userId}/edit`);
  };

  const onModalClose = (): void => {
    setIsModalOpen(false);
  };

  const onRenderColumn = (
    item: IAccessControlUser,
    index?: number,
    column?: IColumn
  ): React.ReactElement => {
    const fieldContent = item[column?.fieldName as keyof IAccessControlUser] as string;
    if (column?.key == "id") {
      return <StyledSpan>{fieldContent}</StyledSpan>;
    }

    return <span>{fieldContent}</span>;
  };

  const onRenderRow = (props?: IDetailsRowProps, defaultRender?: any): React.ReactElement => {
    const selectedUser = props!.item as IAccessControlUser;

    return defaultRender!({
      ...props,
      item: selectedUser,
      onClick: () => onClickEvent(selectedUser.id, selectedUser),
    });
  };

  return (
    <>
      <DetailsList
        items={data}
        columns={tenant === TenantTypesEnum.MVP ? mvpColumns : columns}
        selectionMode={SelectionMode.none}
        getKey={(item: any): string => `${(item as IAccessControlUser).id}`}
        setKey="none"
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        onRenderRow={onRenderRow}
        onRenderItemColumn={onRenderColumn}
        enterModalSelectionOnTouch={true}
        styles={detailListStyles}
      />
      <PaginationRow>
        <Pagination
          total={totalCount}
          onChange={(page) => actions.updateCurrentPage(page)}
          showTitle={false}
          current={currentPage}
          prevIcon={ChevronIcon("ChevronLeft")}
          nextIcon={ChevronIcon("ChevronRight")}
          pageSize={pageSize}
          showSizeChanger
        />
        <Dropdown
          options={[
            { key: 16, text: "Show 16 results" },
            { key: 32, text: "Show 32 results" },
            { key: 48, text: "Show 48 results" },
          ]}
          onChange={(_e, op): void => {
            actions.updatePageSize(op?.key as number);
          }}
          selectedKey={pageSize}
          className="pagination-dropdown"
          title="Page Size Dropdown"
        />
      </PaginationRow>
      <Modal
        titleAriaId="Access Control User Details"
        isOpen={isModalOpen}
        isBlocking={false}
        containerClassName={modalStyles}
        topOffsetFixed
      >
        <ModalHeader>
          <Text block styles={modalHeaderStyles}>
            Admin User Details
          </Text>
          <IconButton
            styles={iconButtonStyles}
            iconProps={{ iconName: "Cancel" }}
            ariaLabel="Close popup modal"
            onClick={onModalClose}
          />
        </ModalHeader>
        <ModalContent>
          <AccessControlDetails />
        </ModalContent>
      </Modal>
    </>
  );
};

/**
 * Maps AccessList to redux state
 * @param {AppState} state The App state.
 */
const mapStateToProps = (state: AppState, ownProps: IAccessControlListProps) => ({
  data: ownProps.data,
});

/**
 * Maps Component's dispatch/handler props to redux state
 * @param {Dispatch} dispatch The dispatch.
 */
const mapDispatchToProps = (dispatch: Dispatch<AccessControlActions.AccessControlActionTypes>) => ({
  actions: {
    loadCLRegions: bindActionCreators(ClRegionActions.getClRegions, dispatch),
    updateCurrentPage: bindActionCreators(AccessControlActions.updateCurrentPage, dispatch),
    updatePageSize: bindActionCreators(AccessControlActions.updatePageSize, dispatch),
    updateSelectedUserId: bindActionCreators(AccessControlActions.updateSelectedUserId, dispatch),
    updateSelectedUser: bindActionCreators(AccessControlActions.updateSelectedUser, dispatch),
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AccessControlList);
